/**
 * QR Code Linking Component
 * Displays a QR code for iOS app to scan and link to participant account
 * Includes WebSocket listener for real-time linking detection
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Link,
} from "@mui/material";
import { CheckCircle, Refresh, Email, ArrowBack } from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import { analyticsService } from "../../services/analyticsService";

interface QRCodeLinkingProps {
  participantId: string;
  studyCode: string;
  email: string;
  onLinkingComplete: () => void;
  onFallbackRequest: () => void;
  onBack?: () => void;
}

type LinkingState = "loading" | "showing_qr" | "linked" | "expired" | "error";

const QRCodeLinking: React.FC<QRCodeLinkingProps> = ({
  participantId,
  studyCode,
  email,
  onLinkingComplete,
  onFallbackRequest,
  onBack,
}) => {
  const [state, setState] = useState<LinkingState>("loading");
  const [qrData, setQrData] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [error, setError] = useState<string>("");
  const wsRef = useRef<WebSocket | null>(null);
  const expiryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const linkingTokenGeneratedAt = useRef<string | null>(null);
  const onLinkingCompleteRef = useRef(onLinkingComplete);

  // Keep the callback ref updated
  useEffect(() => {
    onLinkingCompleteRef.current = onLinkingComplete;
  }, [onLinkingComplete]);

  const generateToken = async () => {
    try {
      setState("loading");
      setError("");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3002"}/api/participants/generate-linking-token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            participantId,
            studyCode,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to generate linking token");
      }

      setQrData(data.qrData);
      setExpiresAt(new Date(data.expiresAt));
      setState("showing_qr");

      // Store when we generated the token (for polling comparison)
      linkingTokenGeneratedAt.current = new Date().toISOString();

      // Set up expiry timer
      const timeUntilExpiry = new Date(data.expiresAt).getTime() - Date.now();
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
      }
      expiryTimerRef.current = setTimeout(() => {
        setState("expired");
      }, timeUntilExpiry);
    } catch (err: any) {
      setError(err.message || "Failed to generate QR code");
      setState("error");
    }
  };

  const connectWebSocket = () => {
    // Don't reconnect if already connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      return;
    }

    // Determine WebSocket URL based on API URL
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3002";
    const wsProtocol = apiUrl.startsWith("https") ? "wss" : "ws";
    const wsHost = apiUrl.replace(/^https?:\/\//, "");
    const wsUrl = `${wsProtocol}://${wsHost}/ws/linking`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        // Subscribe to updates for this participant
        ws.send(
          JSON.stringify({
            type: "subscribe",
            participantId,
          })
        );
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "linking_complete" && data.participantId === participantId) {
            setState("linked");
            analyticsService.trackParticipantHealthkitLinked({ linkingMethod: 'websocket' });
            // Brief delay to show success state before proceeding
            setTimeout(() => {
              onLinkingCompleteRef.current();
            }, 1500);
          }
        } catch (err) {
          console.error("WebSocket message parse error:", err);
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      ws.onclose = () => {};

      wsRef.current = ws;
    } catch (err) {
      console.error("Failed to connect WebSocket:", err);
    }
  };

  // Polling fallback - check if linking was completed
  // This handles cases where WebSocket doesn't work (e.g., iOS app hitting production while frontend is on localhost)
  const checkLinkingStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3002"}/api/participants/check-linking-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            participantId,
            studyCode,
          }),
        }
      );

      const data = await response.json();

      if (data.success && data.linked) {
        // Check if linking happened after we generated the QR code
        const verifiedAt = new Date(data.linkingTokenVerifiedAt);
        const generatedAt = new Date(linkingTokenGeneratedAt.current || 0);

        if (verifiedAt > generatedAt) {
          setState("linked");
          analyticsService.trackParticipantHealthkitLinked({ linkingMethod: 'polling' });
          // Stop polling
          if (pollingTimerRef.current) {
            clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = null;
          }
          // Brief delay to show success state before proceeding
          setTimeout(() => {
            onLinkingCompleteRef.current();
          }, 1500);
        }
      }
    } catch (err) {
      console.error("Error checking linking status:", err);
    }
  };

  const startPolling = () => {
    // Poll every 3 seconds as a fallback
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
    }
    pollingTimerRef.current = setInterval(checkLinkingStatus, 3000);
  };

  // Initialize on mount
  useEffect(() => {
    generateToken();
    connectWebSocket();
    startPolling();

    return () => {
      // Cleanup
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegenerateToken = () => {
    generateToken();
  };

  // Loading state
  if (state === "loading") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 4,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Generating QR code...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (state === "error") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 4,
        }}
      >
        <Alert severity="error" sx={{ mb: 3, maxWidth: 400 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={handleRegenerateToken}
          startIcon={<Refresh />}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  // Linked state (success)
  if (state === "linked") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 4,
        }}
      >
        <CheckCircle sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Device Linked Successfully
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Redirecting to complete your profile...
        </Typography>
      </Box>
    );
  }

  // Expired state
  if (state === "expired") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 4,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          QR Code Expired
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          The QR code has expired. Please generate a new one.
        </Typography>
        <Button
          variant="contained"
          onClick={handleRegenerateToken}
          startIcon={<Refresh />}
        >
          Generate New QR Code
        </Button>
      </Box>
    );
  }

  // Showing QR state (default)
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 2,
      }}
    >
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Link Your Device
      </Typography>

      {/* QR Code */}
      <Box
        sx={{
          p: 3,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow: 1,
          mb: 3,
        }}
      >
        {qrData && (
          <QRCodeSVG
            value={qrData}
            size={200}
            level="M"
            includeMargin={false}
          />
        )}
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
        Scan with the StudySync app
      </Typography>

      {expiresAt && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 3 }}>
          Code expires in {Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / 60000))} minutes
        </Typography>
      )}

      {/* Divider */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 300,
          borderTop: "1px solid",
          borderColor: "divider",
          my: 3,
        }}
      />

      {/* Fallback link */}
      <Link
        component="button"
        variant="body2"
        onClick={onFallbackRequest}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "text.secondary",
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        <Email fontSize="small" />
        Can't scan? Get a code instead
      </Link>

      {/* Back button */}
      {onBack && (
        <Button
          variant="outlined"
          onClick={onBack}
          startIcon={<ArrowBack />}
          sx={{ mt: 3 }}
        >
          Back
        </Button>
      )}
    </Box>
  );
};

export default QRCodeLinking;
